import { useCallback, useEffect, useMemo, useState } from "react";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import axios from "../../../api/axios";
import {
  NOTIFICATIONS_ENDPOINT,
  NOTIFICATIONS_MARK_READ_ENDPOINT,
  NOTIFICATIONS_UNREAD_COUNT_ENDPOINT,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { NotificationItem } from "../../../types/messages";
import { formatDateTime } from "../../../utils";

const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationsListResponse = {
  results?: NotificationItem[];
};

type NotificationsUnreadCountResponse = {
  count: number;
};

export const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewNotifications = useHasPermission("messaging.view_notifications");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const open = Boolean(anchorEl);

  const fetchUnreadCount = useCallback(async () => {
    if (!canViewNotifications) {
      setUnreadCount(0);
      return;
    }

    try {
      const { data } = await axios.get<NotificationsUnreadCountResponse>(
        NOTIFICATIONS_UNREAD_COUNT_ENDPOINT
      );
      setUnreadCount(Number(data?.count) || 0);
    } catch {
      setUnreadCount(0);
    }
  }, [canViewNotifications]);

  const fetchNotifications = useCallback(async () => {
    if (!canViewNotifications) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get<NotificationsListResponse>(NOTIFICATIONS_ENDPOINT, {
        params: { page_size: NOTIFICATIONS_PAGE_SIZE },
      });
      const nextItems = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];
      setNotifications(nextItems);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [canViewNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!canViewNotifications) {
      return;
    }

    try {
      await axios.post(NOTIFICATIONS_MARK_READ_ENDPOINT);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch {
      // Best effort; keep UI responsive even if mark-read fails.
    }
  }, [canViewNotifications]);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!canViewNotifications) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void fetchUnreadCount();
      if (open) {
        void fetchNotifications();
      }
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [canViewNotifications, fetchNotifications, fetchUnreadCount, open]);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    await Promise.all([fetchNotifications(), markAllAsRead()]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getRelatedJobId = useCallback((notification: NotificationItem) => {
    const value = notification.metadata?.job_id;
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string" && /^\d+$/.test(value)) {
      return Number(value);
    }
    return null;
  }, []);

  const handleNotificationClick = useCallback(
    (notification: NotificationItem) => {
      const relatedJobId = getRelatedJobId(notification);
      if (!relatedJobId) {
        return;
      }

      setAnchorEl(null);
      navigate(`/service/jobs/${relatedJobId}`);
    },
    [getRelatedJobId, navigate]
  );

  const listContent = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (notifications.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
          {t("No notifications yet")}
        </Typography>
      );
    }

    return (
      <List disablePadding>
        {notifications.map((notification) => (
          <ListItem key={notification.id} alignItems="flex-start" disablePadding divider>
            <ListItemButton
              onClick={() => handleNotificationClick(notification)}
              disabled={!getRelatedJobId(notification)}
              sx={{
                alignItems: "flex-start",
                cursor: getRelatedJobId(notification) ? "pointer" : "default",
              }}
            >
              <ListItemText
                primary={notification.body}
                secondary={formatDateTime(notification.created_at)}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: notification.is_read ? 400 : 600,
                }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [getRelatedJobId, handleNotificationClick, loading, notifications, t]);

  if (!canViewNotifications) {
    return null;
  }

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t("Notifications")}
        size="small"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 360, maxWidth: "90vw" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle2">{t("Notifications")}</Typography>
          </Box>
          {listContent}
        </Box>
      </Popover>
    </>
  );
};
